import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import { NameWithExternalVendorIcons } from 'components/NameWithExternalVendorIcons/NameWithExternalVendorIcons'
import { translate } from 'i18n/i18n'
import { irrigationEventsColorMap } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { getIrrigationZoneEmitterExternalVendorNames } from 'utils/getIrrigationZoneEmitterExternalVendorNames'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { checkIfDuplicateEmitters } from './checkIfDuplicateEmitters'

const fontSize = 12

const colorStyles = (color: string, fontSize: number) => ({
  margin: '0px 4px 0px 10px',
  height: fontSize,
  width: fontSize,
  background: color,
  display: 'inline-block',
  borderRadius: '3px',
})

export const IrrigationEventsLegendItems = ({
  emitters,
  hasRain,
  hasWaterEvents,
}: {
  emitters: TIrrigationZoneEmitterId[]
  hasRain: boolean
  hasWaterEvents: boolean
}) => {
  if (emitters.length === 0 && !hasRain) return null

  const properties = fieldAssetStore.getState().properties
  const selectedProperty = selectedFieldAssetsStore.getState().property

  const irrigationZoneEmittersForProperty =
    (selectedProperty && properties?.[selectedProperty]?.irrigationZoneEmitters) || {}

  const pointsForProperty = Object.values((selectedProperty && properties?.[selectedProperty]?.points) || {})
  const hasDuplicateEmitters = checkIfDuplicateEmitters(emitters)

  return (
    <>
      {!hasWaterEvents && (
        <div css={{ margin: '20px 0 10px', fontSize, textAlign: 'center' }}>
          {translate.phrases.banyanApp('No water events in this date range')}
        </div>
      )}
      <div
        css={{
          marginLeft: 'auto',
          margin: '10px 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {emitters
          .sort((a, b) => {
            const emitterA = irrigationZoneEmittersForProperty?.[a]?.name
            const emitterB = irrigationZoneEmittersForProperty?.[b]?.name

            return alphabeticalSort(emitterA, emitterB)
          })
          .sort((a, b) => {
            const emitterA = getIdAndEmitterTypeFromZoneEmitterTypeKey(a).emitterType
            const emitterB = getIdAndEmitterTypeFromZoneEmitterTypeKey(b).emitterType

            return alphabeticalSort(emitterA, emitterB)
          })
          .map((legendItem, i) => {
            const { emitterType } = getIdAndEmitterTypeFromZoneEmitterTypeKey(legendItem)
            const irrigationZoneEmitter = irrigationZoneEmittersForProperty?.[legendItem]
            const zoneName = irrigationZoneEmitter?.name

            const externalVendorNames = irrigationZoneEmitter
              ? getIrrigationZoneEmitterExternalVendorNames({
                  irrigationZoneEmitter,
                  points: pointsForProperty,
                })
              : []

            return (
              <div css={{ display: 'flex', alignItems: 'center' }} key={`${legendItem}_${i}`}>
                <span
                  css={{
                    ...colorStyles(irrigationEventsColorMap[emitterType], fontSize),
                  }}
                />
                <span css={{ fontSize, display: 'flex', gap: 3 }}>
                  <span>{mapApiEmitterTypeToLabel(emitterType)}</span>
                  {hasDuplicateEmitters && zoneName ? (
                    <span>
                      <NameWithExternalVendorIcons
                        name={` (${zoneName})`}
                        externalVendorNames={externalVendorNames}
                        iconSize={fontSize}
                      />
                    </span>
                  ) : null}
                </span>
              </div>
            )
          })}
        {hasRain && (
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <span css={{ ...colorStyles(irrigationEventsColorMap.rain, fontSize) }} />
            <span css={{ fontSize: '12px' }}>{translate.phrases.banyanApp('Rain')}</span>
          </div>
        )}
      </div>
    </>
  )
}
